import { memo, useEffect } from 'react';

import { useSearchParams } from 'react-router-dom';

import { checkToken } from './loader.service';
import { Spinner } from '@/components/ui/spinner';
import NoOrganizations from '@/components/ui/no-organizations';
import { LoginConstants } from '../../config/keyClock.config';
import { useScopeBootstrap } from '../../hooks/useScopeBootstrap';

const Loader = memo(function Loader() {
  const [searchParams] = useSearchParams();
  const { noOrganizations, resolveScopeAndLand } = useScopeBootstrap();

  useEffect(() => {
    // An invited user routed here from the Keycloak set-password page with
    // ?idp=google. Stash it before checkToken() fires so the Keycloak redirect
    // carries kc_idp_hint (see loader.service / getIdpHint). Allowlisted to
    // 'google' so the value can't inject an arbitrary alias into the auth URL.
    const idpHint: string | null = searchParams.get('idp');
    if (idpHint === 'google') {
      sessionStorage.setItem(LoginConstants.IDP_HINT, idpHint);
    }

    const code: string | null = searchParams.get('navigateTo');
    const propertyId: string | null = searchParams.get('orderNumber');
    if (code != null && propertyId != null) {
      sessionStorage.setItem('navigateTo', code);
      sessionStorage.setItem('orderId', propertyId);
    }

    // No token ⇒ checkToken() has just kicked off the Keycloak flow and
    // reported so; there is nothing to scope yet.
    if (checkToken()) return;

    // Everything this effect used to do for itself — the role-branched
    // OrgData fetch, the picker routing, the intendedUrl consumption and the
    // DV-3175 empty-list dead end — belongs to the shared resolution step now
    // (DV-3012 §3.1 / D1). It was one of three copies; CallBack held the
    // second and the pickers the third.
    void resolveScopeAndLand();
    // Run once at mount. searchParams is intentionally omitted from the deps —
    // this is a one-shot bootstrap effect, not a reactive subscription.
  }, []);

  if (noOrganizations) {
    return <NoOrganizations />;
  }

  return (
    <div className="flex flex-col items-center justify-center h-screen">
      <Spinner size="lg" />
      <span className="mt-2.5 text-xl text-foreground">
        Please wait..
      </span>
    </div>
  );
});

export default Loader;
